import {
  Component,
  inject,
  LOCALE_ID,
  ChangeDetectionStrategy,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { displayName, optionalName } from '../../../../validators/name-rules';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UndeliveredReasonsService } from '../../../../services/undelivered-reasons/undelivered-reasons.service';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Error } from '../../../public-user-component/auth/shared/error/error';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import {
  arabicOnlyValidator,
  englishOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
} from '../undelivered-reasons.validators';
import { SCOPES } from '../../../../services/scope/scope-constants'; 
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';

@Component({
  selector: 'app-undelivered-reasons-create-component',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SuccessfulNotification,
    Error,
    ConfirmationDialog,
    SubmitButton,
    HasScopeDirective,
  ],
  templateUrl: './undelivered-reasons-create-component.html',
  styleUrl: './undelivered-reasons-create-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndeliveredReasonsCreateComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private undeliveredReasonsService = inject(UndeliveredReasonsService);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  protected readonly SCOPES = SCOPES;

  reasonForm!: FormGroup;

  // Signals State
  showConfirmModal = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  showToast = signal<boolean>(false);
  toastMessage = signal<string>('');
  apiError = signal<string>('');

  // Computed Properties for Locale
  isArabic = computed(() => typeof this.locale === 'string' && this.locale.startsWith('ar'));

  // Computed Properties for Confirmation Dialog Translations
  modalTitle = computed(() => (this.isArabic() ? 'تأكيد الإرسال' : 'Confirm Submission'));
  modalDescription = computed(() =>
    this.isArabic()
      ? 'هل أنت تأكد من أنك تريد إنشاء هذا السبب؟'
      : 'Are you sure you want to create this reason?'
  );
  modalConfirmText = computed(() => (this.isArabic() ? 'تأكيد' : 'Confirm'));
  modalCancelText = computed(() => (this.isArabic() ? 'إلغاء' : 'Cancel'));

  constructor() {
    this.initForm();
  }

  private initForm(): void {
    this.reasonForm = this.fb.group({
      reasonAr: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
          arabicOnlyValidator(),
          noLeadingTrailingSpacesValidator(),
          noNumbersValidator(),
        ],
      ],
      reasonEn: [
        '',
        [
          Validators.minLength(3),
          Validators.maxLength(255),
          englishOnlyValidator(),
          noLeadingTrailingSpacesValidator(),
          noNumbersValidator(),
        ],
      ],
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.reasonForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSubmit(): void {
    if (this.reasonForm.invalid) {
      this.reasonForm.markAllAsTouched();
      return;
    }
    this.apiError.set('');
    this.showConfirmModal.set(true);
  }

  closeConfirmModal(): void {
    this.showConfirmModal.set(false);
  }

  confirmSubmit(): void {
    this.showConfirmModal.set(false);
    this.isSubmitting.set(true);
    this.apiError.set('');

    const payload = {
      titleEn: optionalName(this.reasonForm.value.reasonEn),
      titleAr: this.reasonForm.value.reasonAr.trim(),
    };

    this.undeliveredReasonsService
      .createReason(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubmitting.set(false);

          const reasonName = displayName(this.isArabic(), payload.titleAr, payload.titleEn);
          this.toastMessage.set(
            this.isArabic()
              ? `تم إنشاء سبب الإلغاء '${reasonName}' بنجاح`
              : `Undelivered reason '${reasonName}' created successfully`
          );

          this.showToast.set(true);

          setTimeout(() => {
            this.showToast.set(false);
            this.navigateBack();
          }, 3000);
        },
        error: (err) => {
          this.isSubmitting.set(false);

          const hasFieldError = !!(err.error?.errors?.titleAr || err.error?.errors?.titleEn);

          if (err.error?.errors?.titleAr) {
            this.reasonForm.get('reasonAr')?.setErrors({ duplicate: true });
          }
          if (err.error?.errors?.titleEn) {
            this.reasonForm.get('reasonEn')?.setErrors({ duplicate: true });
          }

          if (!hasFieldError) {
            this.apiError.set(
              this.isArabic()
                ? err.error?.messageAr || 'حدث خطأ أثناء إنشاء السبب. حاول مرة أخرى.'
                : err.error?.messageEn || 'An error occurred while creating the reason. Please try again.'
            );
          }
        },
      });
  }

  closeToast(): void {
    this.showToast.set(false);
  }

  onCancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }
}